import React, { useState } from "react";
import { Typography, Box, Button, Alert } from "@mui/material";
import CommunicationList from "../components/CommunicationList";
import GetCommunicationByIdModal from "../components/GetCommunicationByIdModal";
import AddModal from "../components/AddModal";
import EditModal from "../components/EditModal";
import DetailModal from "../components/DetailModal";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../components/ConfirmationModal";
import { Communication } from "../../../data/Communication";
import useCommunication from "../hooks/useCom";
import { sendEmail } from "../../../services/communicationAPI";

const CommunicationsListPage: React.FC = () => {
    const {
        communication,
        loading,
        error,
        handleAdd: handleAddToList,
        handleUpdate: handleUpdateInList,
        handleDelete,
        setCommunication,
    } = useCommunication();

    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isDetailModalOpen, setDetailModalOpen] = useState(false);
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [selectedCommunication, setSelectedCommunication] = useState<Communication | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleOpenAddModal = () => setAddModalOpen(true);
    const handleCloseAddModal = () => setAddModalOpen(false);

    const handleOpenEditModal = (type: Communication) => {
        setSelectedCommunication(type);
        setEditModalOpen(true);
    };
    const handleCloseEditModal = () => {
        setSelectedCommunication(null);
        setEditModalOpen(false);
    };

    const handleOpenConfirmationModal = (type: Communication) => {
        setSelectedCommunication(type);
        setConfirmationModalOpen(true);
    };
    const handleCloseConfirmationModal = () => {
        setSelectedCommunication(null);
        setConfirmationModalOpen(false);
    };

    const handleViewDetails = (type: Communication) => {
        setSelectedCommunication(type);
        setDetailModalOpen(true);
    };

    const handleAdd = async (
        communicationId: string,
        title: string,
        comTypeId: string,
        eventName: string,
        status: string,
        composedBy: string,
        composedTimeStamp: string,
        sendDateAsString: string,
        message: string
    ) => {
        const newType: Partial<Communication> = {
            communicationId,
            title,
            comTypeId,
            eventName,
            status,
            composedBy,
            composedTimeStamp,
            sendDate: sendDateAsString,
            message,
        };

        await handleAddToList(newType);
        handleCloseAddModal();
    };

    const handleUpdate = async (updatedType: {
        communicationId: string;
        title: string;
        comTypeId: string;
        eventName: string;
        status: string;
        composedTimeStamp: string;
        reviewedTimeStamp: string;
        reviewedBy: string;
        sendDateAsString: string;
        message: string;
    }) => {
        const newType: Partial<Communication> = {
            communicationId: updatedType.communicationId,
            title: updatedType.title,
            comTypeId: updatedType.comTypeId,
            eventName: updatedType.eventName,
            status: updatedType.status,
            composedBy: updatedType.composedTimeStamp,
            composedTimeStamp: updatedType.composedTimeStamp,
            reviewedBy: updatedType.reviewedBy,
            reviewedTimeStamp: updatedType.reviewedTimeStamp,
            sendDate: updatedType.sendDateAsString,
            message: updatedType.message,
        };

        await handleUpdateInList(newType);
    };

    const handleConfirmDelete = async () => {
        if (selectedCommunication) {
            setIsDeleting(true);
            try {
                await handleDelete(selectedCommunication.communicationId);
                handleCloseConfirmationModal();
            } catch (err) {
                console.error('Delete failed:', err);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const handleSendEmail = async (comm: Communication) => {
        try {
            await sendEmail(comm.title, comm.message, comm.recipients || []);
            const updated = {
                ...comm,
                sendDate: new Date().toISOString(),
            };
            setCommunication((prev: Communication[]) =>
                prev.map((c) =>
                    c.communicationId === updated.communicationId ? updated : c
                )
            );
        } catch (err: any) {
            alert("❌ Failed to send email: " + err.message);
        }
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ padding: 3 }}>
                {loading && <Typography>Loading...</Typography>}
                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        Communications
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate("/communication-types")}
                            sx={{
                                backgroundColor: "#2196F3 !important",
                                color: "white !important",
                                "&:hover": { backgroundColor: "#1976D2 !important" },
                                padding: "6px 12px",
                                fontSize: "0.875rem",
                            }}
                        >
                            Manage Communication Types
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleOpenAddModal}
                            sx={{
                                backgroundColor: "#4CAF50 !important",
                                color: "white !important",
                                "&:hover": { backgroundColor: "#45A049 !important" },
                            }}
                        >
                            Add Communication
                        </Button>
                    </Box>
                </Box>
            </Box>

            <CommunicationList
                communications={communication}
                onEdit={handleOpenEditModal}
                onDeleteClick={handleOpenConfirmationModal}
                onSendEmail={handleSendEmail}
                onViewDetails={handleViewDetails}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />

            <AddModal
                isOpen={isAddModalOpen}
                onClose={handleCloseAddModal}
                onAdd={handleAdd}
                existingCommunication={communication}
            />

            <EditModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                selectedCommunication={selectedCommunication}
                onSave={handleUpdate}
            />

            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={handleCloseConfirmationModal}
                onConfirm={handleConfirmDelete}
                title="Delete Communication"
                message={`Are you sure you want to delete "${selectedCommunication?.title}"?`}
                isLoading={isDeleting}
            />

            <DetailModal
                isOpen={isDetailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                selectedCommunication={selectedCommunication}
            />
        </Box>
    );
};

export default CommunicationsListPage;
